import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { User } from '@/types'
import * as api from '@/services/api'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (googleJwt: string) => Promise<void>
  logout: () => void
  fetchUser: () => Promise<void>
  updateUser: (userData: Partial<User>) => Promise<void>
  loginAsDevelopmentUser: () => Promise<void>
  loginAsTestUser: () => Promise<void>
  loginAsAnonymousUser: () => Promise<void>
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (googleJwt) => {
        set({ isLoading: true, error: null })
        try {
          const { token, user } = await api.loginWithGoogle(googleJwt)
          set({ user, token, isAuthenticated: true, isLoading: false })
          api.setAuthToken(token)
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Login failed.'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false })
        api.setAuthToken(null)
      },

      fetchUser: async () => {
        if (!get().isAuthenticated) return
        set({ isLoading: true, error: null })
        try {
          const user = await api.getCurrentUser()
          set({ user, isLoading: false })
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch user.'
          set({ error: errorMessage, isLoading: false })
          get().logout() // Logout if fetching user fails
        }
      },

      updateUser: async (userData) => {
        if (!get().isAuthenticated) return
        set({ isLoading: true, error: null })
        try {
          const updatedUser = await api.updateCurrentUser(userData)
          set({ user: updatedUser, isLoading: false })
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to update user.'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      loginAsDevelopmentUser: async () => {
        set({ isLoading: true, error: null })
        const devUser: User = {
          id: 'dev-user-id-01',
          email: 'developer@assist-api.com',
          name: 'Dev User',
          picture:
            'https://img.usecurling.com/ppl/medium?gender=male&seed=developer',
        }
        const devToken = 'this-is-a-mock-jwt-for-development-only'

        set({
          user: devUser,
          token: devToken,
          isAuthenticated: true,
          isLoading: false,
        })
        api.setAuthToken(devToken)
      },

      loginAsTestUser: async () => {
        set({ isLoading: true, error: null })
        const testUser: User = {
          id: 'test-user-id-01',
          email: 'test@assist-api.com',
          name: 'Test User',
          picture:
            'https://img.usecurling.com/ppl/medium?gender=female&seed=tester',
        }
        const testToken = 'this-is-a-mock-jwt-for-testing-only'

        set({
          user: testUser,
          token: testToken,
          isAuthenticated: true,
          isLoading: false,
        })
        api.setAuthToken(testToken)
      },

      loginAsAnonymousUser: async () => {
        set({ isLoading: true, error: null })
        const anonymousUser: User = {
          id: 'anonymous-user-id-01',
          email: 'anonymous@assist-api.com',
          name: 'Anonymous User',
          picture: 'https://img.usecurling.com/ppl/medium?seed=anonymous',
        }
        const anonymousToken = 'this-is-a-mock-jwt-for-anonymous-user-only'

        set({
          user: anonymousUser,
          token: anonymousToken,
          isAuthenticated: true,
          isLoading: false,
        })
        api.setAuthToken(anonymousToken)
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrate: (state) => {
        if (state.token) {
          api.setAuthToken(state.token)
        }
      },
    },
  ),
)

export default useAuthStore
